import { SystemRole } from '@qualicloud/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  IconBell,
  IconBuilding,
  IconLogout,
  IconMenu,
  IconMoon,
  IconQualiCloud,
  IconSearch,
  IconSettings,
  IconSun,
  IconUser,
  Input,
  DropdownMenu,
  cn,
  focusRing,
} from '@/design-system';
import { notificationsApi, queryKeys } from '@/lib/api';
import { SYSTEM_ROLE_LABEL } from '@/lib/i18n';
import { useAuth } from '@/providers/auth-provider';
import { useTheme } from '@/providers/theme-provider';
import { OrganizationSwitcher } from './organization-switcher';

/**
 * Barra superior: busca global, seletor de organização, tema, notificações e
 * menu do usuário.
 *
 * ACESSIBILIDADE:
 *  · `<header role="banner">` implícito pelo `<header>` de nível superior;
 *  · a busca é um `<form role="search">` com `<label>` oculto — sem isso o
 *    campo é anunciado apenas como "caixa de edição";
 *  · o contador de não lidas NÃO fica só no ponto vermelho: o `aria-label` do
 *    botão diz "Notificações, 3 não lidas", e o número visível tem `qc-tabular`;
 *  · atalho "/" foca a busca, com a convenção anunciada no `title` do campo.
 */
export function Topbar({
  onToggleNav,
  navOpen,
}: {
  readonly onToggleNav: () => void;
  readonly navOpen: boolean;
}) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');

  const { data: unread } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationsApi.unreadCount(),
    // Só depois de a sessão existir. A barra monta junto com o shell, antes de
    // `bootstrapSession()` colocar o access token em memória, e a consulta saía
    // sem `authorization`: 401 em TODA navegação, no console do navegador e no
    // log do gateway. Um 401 recorrente e esperado é ruído que esconde o 401 de
    // verdade — e, num sistema em que sessão perdida é evento de trilha, esse é
    // o alarme que não pode virar paisagem.
    enabled: Boolean(user),
    // Um minuto é suficiente: notificação em eQMS não é mensageria instantânea,
    // e um polling agressivo multiplicaria carga sem ganho de uso.
    refetchInterval: 60_000,
    retry: false,
  });

  const unreadCount = unread?.unreadCount ?? 0;

  // Atalho "/" para focar a busca — só quando o foco não está em um campo.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const search = term.trim();
    void navigate({
      to: '/documentos',
      search: search ? { search, page: 1 } : { page: 1 },
    });
  };

  return (
    <header className="sticky top-0 z-sticky flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-3 lg:px-4">
      <Button
        size="icon"
        variant="ghost"
        iconLabel={navOpen ? 'Fechar a navegação' : 'Abrir a navegação'}
        aria-expanded={navOpen}
        onClick={onToggleNav}
        className="lg:hidden"
      >
        <IconMenu size={18} />
      </Button>

      <Link
        to="/"
        className={cn('flex shrink-0 items-center gap-2 rounded font-semibold text-content', focusRing)}
      >
        {/* A marca da empresa, não uma inicial num quadrado: a nuvem com a
            lâmpada é o logotipo da QualiCloud, e herda a cor do token da marca
            (turquesa no escuro, petróleo no claro). */}
        <IconQualiCloud size={26} className="text-brand" />
        <span className="hidden text-base sm:inline">QualiCloud</span>
        <span className="qc-sr-only">— eQMS, página inicial</span>
      </Link>

      {/* ── Busca global ─────────────────────────────────────────────────── */}
      <form role="search" onSubmit={submitSearch} className="mx-auto w-full max-w-md">
        <label htmlFor="global-search" className="qc-sr-only">
          Buscar documentos por nome ou código
        </label>
        <Input
          id="global-search"
          ref={searchRef}
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Buscar documentos…"
          title='Pressione "/" para focar a busca'
          leadingIcon={<IconSearch size={15} />}
          inputSize="sm"
        />
      </form>

      <div className="ml-auto flex shrink-0 items-center gap-1">
        {user?.role === SystemRole.PLATFORM_ADMIN ? <OrganizationSwitcher /> : null}

        <Button
          size="icon"
          variant="ghost"
          iconLabel={theme === 'dark' ? 'Mudar para o tema claro' : 'Mudar para o tema escuro'}
          onClick={toggleTheme}
        >
          {theme === 'dark' ? <IconSun size={17} /> : <IconMoon size={17} />}
        </Button>

        <Link
          to="/notificacoes"
          aria-label={
            unreadCount > 0
              ? `Notificações, ${unreadCount} não ${unreadCount === 1 ? 'lida' : 'lidas'}`
              : 'Notificações, nenhuma não lida'
          }
          className={cn(
            'relative flex h-[2.375rem] w-[2.375rem] items-center justify-center rounded-md text-content-muted',
            'hover:bg-surface-sunken hover:text-content',
            focusRing,
          )}
        >
          <IconBell size={17} />
          {unreadCount > 0 ? (
            /* O número é exibido, não só o ponto: "tem algo novo" e "tem 12
               coisas novas" são informações diferentes. */
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 flex min-w-[1.125rem] items-center justify-center rounded-full bg-danger px-1 text-2xs font-bold text-danger-contrast qc-tabular"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          ) : null}
        </Link>

        <DropdownMenu
          ariaLabel="Menu do usuário"
          trigger={
            <button
              type="button"
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-surface-sunken',
                focusRing,
              )}
            >
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-subtle text-xs font-semibold text-brand"
              >
                {initials(user?.displayName ?? '')}
              </span>
              <span className="hidden min-w-0 sm:block">
                <span className="block max-w-[9rem] truncate text-sm font-medium text-content">
                  {user?.displayName}
                </span>
                <span className="block max-w-[9rem] truncate text-2xs text-content-subtle">
                  {user ? SYSTEM_ROLE_LABEL[user.role] : ''}
                </span>
              </span>
            </button>
          }
          sections={[
            {
              id: 'identity',
              label: user?.username ? `Sessão de ${user.username}` : 'Sessão',
              items: [
                {
                  id: 'profile',
                  label: 'Meu perfil e segurança',
                  icon: <IconUser size={15} />,
                  onSelect: () => void navigate({ to: '/perfil' }),
                },
                {
                  id: 'password',
                  label: 'Trocar senha',
                  icon: <IconSettings size={15} />,
                  onSelect: () => void navigate({ to: '/trocar-senha' }),
                },
              ],
            },
            {
              id: 'org',
              items: [
                {
                  id: 'organization',
                  label: user?.organizationName || 'Organização',
                  icon: <IconBuilding size={15} />,
                  disabled: true,
                  disabledReason: 'organização da sessão atual',
                },
              ],
            },
            {
              id: 'session',
              items: [
                {
                  id: 'logout',
                  label: 'Sair',
                  icon: <IconLogout size={15} />,
                  tone: 'danger',
                  onSelect: () => {
                    void logout().then(() => navigate({ to: '/login' }));
                  },
                },
              ],
            },
          ]}
        />
      </div>
    </header>
  );
}

/** Iniciais do nome, com no máximo duas letras. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}
